import { EmptyView, emptyViewVariants } from '@components'

import { ModuleLayout, useModal } from '@billy/design-system'
import React, { ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { CustomerModal } from '@features/contacts/elements/CustomerModal'
import { CustomerModalSubmitActionType } from '@features/contacts/enums/customerModalSubmitActionType'

import { queryClient } from '../../../../config/queryClient'
import { ModalId } from '../../../../enums/modalId'
import { QueryKeys } from '../../../../enums/queryKeys'
import { useOrganizationSource } from '../../../../hooks'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { useContactsEmberConnection } from '../../context/contactsEmberConnectionContext'
import { defaultContactsQuery, useContactsListFilters } from '../../hooks/useContactsListFilters'
import { useIsContactsListEmpty } from '../../hooks/useIsContactsListEmpty'

interface ContactsEmptyStateProps {
  isCustomer?: boolean
  isSupplier?: boolean
}

const customerModalId = ModalId.CustomerModal

export const ContactsEmptyState = ({
  isCustomer = true,
  isSupplier = false,
}: ContactsEmptyStateProps): ReactElement | null => {
  const { t } = useTranslation()
  const [, setFilters] = useContactsListFilters()
  const isContactsListEmpty = useIsContactsListEmpty({ isCustomer, isSupplier })
  const { open: openCustomerModal } = useModal(customerModalId)
  const { isSourceBilly } = useOrganizationSource()
  const { createNewContact } = useContactsEmberConnection()
  const { url: suppliersMainRoute } = useOrganizationViewUrl(reactRoute.organization.suppliers.getMainRoute())
  const { url: clientsMainRoute } = useOrganizationViewUrl(reactRoute.organization.clients.getMainRoute())
  const shouldRedirectToContactsList = !isContactsListEmpty

  useEffect(() => {
    setFilters(defaultContactsQuery)
  }, [setFilters])

  const { animation, description, title, buttonText } = emptyViewVariants.contacts

  const handleEmptyViewClick = useCallback(() => {
    if (isSupplier || isSourceBilly) {
      createNewContact({ isCustomer, isSupplier })
      return
    }
    openCustomerModal()
  }, [createNewContact, isCustomer, isSourceBilly, isSupplier, openCustomerModal])

  const handleCustomerCreation = useCallback(() => {
    queryClient.invalidateQueries(QueryKeys.ContactsAndContactPersons)
  }, [])

  if (!suppliersMainRoute || !clientsMainRoute) {
    return null
  }

  if (shouldRedirectToContactsList && isCustomer) {
    return <Navigate to={clientsMainRoute} replace />
  }

  if (shouldRedirectToContactsList && isSupplier) {
    return <Navigate to={suppliersMainRoute} replace />
  }

  return (
    <ModuleLayout title={t('contacts.header')}>
      <EmptyView
        animationName={animation}
        onClick={handleEmptyViewClick}
        tButton={buttonText}
        tDescription={description}
        tTitle={title}
        showCTA
      />
      {isCustomer && (
        <CustomerModal
          id={customerModalId}
          onSubmit={handleCustomerCreation}
          size="s"
          submitActionType={CustomerModalSubmitActionType.Create}
        />
      )}
    </ModuleLayout>
  )
}
